import moment from 'moment';
import { parseDateISO, formatDateISO, todayISO } from '../../utils/dateHelpers';
import { CalendarIcon as CalendarIconSvg } from '../../icons/CalendarIcon';
import {
  CalendarIcon,
  DayCell,
  DayNumber,
  DayPrice,
  DaysRow,
  Header,
  MonthLabel,
  NavButton,
  NavControls,
  WeekdayLabel,
  WeekdayRow,
  Wrapper,
} from './WeekCalendar.styles';
import type { DayAvailability, WeekCalendarProps } from './WeekCalendar.types';

function getDefaultWeekdays(): string[] {
  const wd = moment.localeData().weekdaysMin();
  return [...wd.slice(1), wd[0]];
}

function getDefaultMonths(): string[] {
  return moment.localeData().months();
}

function getWeekStartDate(dateStr: string): Date {
  const date = parseDateISO(dateStr);
  const day = date.getDay();
  const offset = (day + 6) % 7;
  const start = new Date(date);
  start.setDate(start.getDate() - offset);
  return start;
}

function formatDayPrice(amount: number, currencyCode: string, locale: string): string {
  const major = amount / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(major);
  } catch {
    return `${currencyCode} ${major}`;
  }
}

export function WeekCalendar({
  activeDate,
  selectedDate,
  availability,
  onDateSelect,
  onNavigate,
  onCalendarClick,
  labels,
}: WeekCalendarProps) {
  const today = todayISO();
  const weekdays = labels?.weekdays ?? getDefaultWeekdays();
  const monthNames = labels?.months ?? getDefaultMonths();
  const locale = labels?.locale ?? 'en-GB';
  const prevWeekLabel = labels?.previousWeek ?? 'Previous week';
  const nextWeekLabel = labels?.nextWeek ?? 'Next week';
  const openCalendarLabel = labels?.openCalendar ?? 'Open calendar';

  const weekStart = getWeekStartDate(activeDate);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    days.push(d);
  }

  const availMap = new Map<string, DayAvailability>();
  availability?.forEach((a) => availMap.set(a.date, a));

  const monthDate = parseDateISO(activeDate);
  const monthLabel = `${monthNames[monthDate.getMonth()]} ${monthDate.getFullYear()}`;

  const handlePrev = () => {
    const prev = new Date(weekStart);
    prev.setDate(prev.getDate() - 7);
    onNavigate(formatDateISO(prev));
  };

  const handleNext = () => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + 7);
    onNavigate(formatDateISO(next));
  };

  return (
    <Wrapper>
      <Header>
        <MonthLabel>{monthLabel}</MonthLabel>
        <NavControls>
          <NavButton type="button" aria-label={prevWeekLabel} onClick={handlePrev}>
            ‹
          </NavButton>
          <NavButton type="button" aria-label={nextWeekLabel} onClick={handleNext}>
            ›
          </NavButton>
          {onCalendarClick && (
            <CalendarIcon type="button" aria-label={openCalendarLabel} onClick={onCalendarClick}>
              <CalendarIconSvg size={18} />
            </CalendarIcon>
          )}
        </NavControls>
      </Header>
      <WeekdayRow>
        {weekdays.map((wd) => (
          <WeekdayLabel key={wd}>{wd}</WeekdayLabel>
        ))}
      </WeekdayRow>
      <DaysRow>
        {days.map((date) => {
          const dateStr = formatDateISO(date);
          const dayData = availMap.get(dateStr);
          const isPast = dateStr < today;
          const isUnavailable = isPast || (dayData !== undefined && !dayData.available);
          const isSelected = dateStr === selectedDate;
          const isToday = dateStr === today;
          const showPrice =
            !isUnavailable &&
            dayData?.price !== undefined &&
            dayData.price > 0 &&
            dayData.currencyCode;

          return (
            <DayCell
              key={dateStr}
              type="button"
              {...(isUnavailable ? { 'data-unavailable': '' } : undefined)}
              {...(isSelected ? { 'data-selected': '' } : undefined)}
              {...(isToday ? { 'data-today': '' } : undefined)}
              onClick={() => !isUnavailable && onDateSelect(dateStr)}
              disabled={isUnavailable}
              aria-label={dateStr}
              aria-pressed={isSelected}
            >
              <DayNumber $selected={isSelected}>{date.getDate()}</DayNumber>
              {showPrice && (
                <DayPrice $selected={isSelected}>
                  {formatDayPrice(dayData!.price!, dayData!.currencyCode!, locale)}
                </DayPrice>
              )}
            </DayCell>
          );
        })}
      </DaysRow>
    </Wrapper>
  );
}
